import { ref, computed, watch, type Ref } from 'vue';
import http from '@/services/http';
import { readNestedId } from '@/utils/nestedRelation';

/**
 * Resolve the Location Zone Player cards' `player_data_current_track_code`s
 * into playable track summaries — display name, artist, album, cover art, and
 * the live track id (for click-to-inspect) — batching every code that appears
 * in a render into ONE search instead of a lookup per card.
 *
 * Summaries are cached module-wide by code, like the playlist-name resolver
 * ({@link file:src/composables/api/useResolvedPlaylistNames.ts}): a code that
 * resolved once renders instantly on every later poll, and only codes never
 * seen before are fetched. This is what stops the reload flash — the previous
 * version keyed one query on the JOINED list of visible codes, so a single
 * player advancing to a new track re-keyed the whole batch and every row fell
 * back to raw code + placeholder art while all of them refetched.
 *
 * Entries do expire (tracks are near-immutable, but the cover URL is a
 * presigned link that eventually lapses) — an expired entry keeps serving its
 * old value while the refresh fetch runs, so a refresh never blanks a row.
 * Codes that resolve to nothing (stale check-ins, test data) are cached as
 * misses too, on a shorter clock, so they stop re-querying on every poll but
 * a track uploaded mid-session still shows up within minutes.
 */

export interface CurrentTrackSummary {
    id: number;
    code: string;
    name: string | null;
    artistName: string | null;
    albumName: string | null;
    /**
     * Ids behind the two names, so a surface that plays this track can hand the
     * player bar records to link its artist / album lines to.
     */
    artistId: number | string | null;
    albumId: number | string | null;
    coverUrl: string | null;
}

interface CacheEntry {
    /** The resolved summary, or null when the search found nothing for the code. */
    summary: CurrentTrackSummary | null;
    fetchedAt: number;
}

/** Refresh a resolved summary well inside the presigned cover URL's lifetime. */
const RESOLVED_REFRESH_MS = 30 * 60_000;

/** Retry an unresolved code occasionally — it may be a track uploaded mid-session. */
const UNRESOLVED_RETRY_MS = 5 * 60_000;

const cache = ref(new Map<string, CacheEntry>());
const inFlight = new Set<string>();
let queue = new Set<string>();
let scheduled = false;

function isFresh(entry: CacheEntry, now: number): boolean {
    const ttl = entry.summary === null ? UNRESOLVED_RETRY_MS : RESOLVED_REFRESH_MS;
    return now - entry.fetchedAt < ttl;
}

function readDisplayString(value: unknown): string | null {
    if (value && typeof value === 'object' && 'display_string' in value) {
        const display = (value as { display_string?: unknown }).display_string;
        return typeof display === 'string' && display.trim() !== '' ? display : null;
    }
    return null;
}

/**
 * One batched search for every code queued this tick. Misses are cached as
 * misses; a failed request caches nothing, so the next poll retries.
 */
async function flush(): Promise<void> {
    const codes = [...queue];
    queue = new Set();
    scheduled = false;
    if (codes.length === 0) {
        return;
    }

    try {
        const response = await http.post('/api/live_track/search', {
            perPage: codes.length,
            withTotal: false,
            filter: {
                ruleScopes: {
                    id: 'root',
                    scopeType: 'and',
                    rules: [
                        {
                            id: 'current-track-codes',
                            column: 'code',
                            criterion: 'contains_any',
                            value: codes,
                            field: null,
                        },
                    ],
                },
            },
        });
        const data = (response.data as { data?: Record<string, unknown>[] }).data ?? [];
        const resolvedByCode = new Map<string, CurrentTrackSummary>();
        for (const track of data) {
            if (typeof track.code !== 'string') continue;
            const code = track.code.toUpperCase();
            resolvedByCode.set(code, {
                id: track.id as number,
                code,
                name: typeof track.name === 'string' && track.name !== '' ? track.name : null,
                artistName: readDisplayString(track.artist),
                albumName: readDisplayString(track.album),
                artistId: readNestedId(track.artist),
                albumId: readNestedId(track.album),
                coverUrl:
                    typeof track.cover_art_signed_url === 'string' && track.cover_art_signed_url !== ''
                        ? track.cover_art_signed_url
                        : null,
            });
        }
        const now = Date.now();
        const next = new Map(cache.value);
        for (const code of codes) {
            next.set(code, { summary: resolvedByCode.get(code) ?? null, fetchedAt: now });
        }
        cache.value = next;
    } catch {
        // Nothing cached — inFlight is cleared below and the next poll re-asks.
    } finally {
        for (const code of codes) {
            inFlight.delete(code);
        }
    }
}

function ask(code: string): void {
    const entry = cache.value.get(code);
    if ((entry && isFresh(entry, Date.now())) || inFlight.has(code)) {
        return;
    }
    inFlight.add(code);
    queue.add(code);

    if (!scheduled) {
        scheduled = true;
        // A microtask, so every code from this render joins the same request.
        void Promise.resolve().then(flush);
    }
}

export function useCurrentTrackSummaries(rows: Ref<Record<string, unknown>[]>) {
    const trackCodes = computed(() => {
        const codes = new Set<string>();
        for (const row of rows.value) {
            const code = row.player_data_current_track_code;
            if (typeof code === 'string' && code.trim() !== '') {
                codes.add(code.trim().toUpperCase());
            }
        }
        return [...codes].sort();
    });

    // Watch the rows, not the code list: a poll that returns the same codes
    // still re-asks (a no-op on cache hits), which is what retries a code
    // whose fetch failed and refreshes entries as they expire.
    watch(
        rows,
        () => {
            for (const code of trackCodes.value) {
                ask(code);
            }
        },
        { immediate: true }
    );

    /**
     * Every summary the cache knows, by code — a superset of the current rows'
     * codes, which is harmless since consumers look up per row.
     */
    const summariesByCode = computed(() => {
        const byCode = new Map<string, CurrentTrackSummary>();
        for (const [code, entry] of cache.value) {
            if (entry.summary !== null) {
                byCode.set(code, entry.summary);
            }
        }
        return byCode;
    });

    /** Summary for one player row's current track, or null when absent/unresolved. */
    function summaryForRow(row: Record<string, unknown>): CurrentTrackSummary | null {
        const code = row.player_data_current_track_code;
        if (typeof code !== 'string' || code.trim() === '') return null;
        return summariesByCode.value.get(code.trim().toUpperCase()) ?? null;
    }

    return { summariesByCode, summaryForRow };
}

/** Test seam — the cache is module-level so it survives between components. */
export function __resetCurrentTrackSummaries(): void {
    cache.value = new Map();
    inFlight.clear();
    queue = new Set();
    scheduled = false;
}
